import castArray from 'lodash/castArray'
import first from 'lodash/first'
import trim from 'lodash/trim'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import React from 'react'

import { BackToTop } from '@Components/BackToTop'
import { SinglePostWrap } from '@Components/CMS/SinglePostWrap'
import { FooterWithGroupVisibility } from '@Components/Footer'
import { HeaderWithGroupVisibility } from '@Components/Header'
import { Page, PageType } from '@Generated/graphql'
import { initializeApolloFromContext } from '@Globals/Apollo'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { PageHOC } from '@Globals/Page/Client'
import { boostFromContext, handleRedirect, pageProps } from '@Globals/Page/Server'
import { PageProps } from '@Globals/Page/Types'
import { getStoreURLByContext } from '@Globals/Stores'
import { OneColumn } from '@Layouts/OneColumn'
import { getSinglePost } from '@Queries/cms/SinglePost.query'

import styles from './index.module.scss'

interface SearchResultsProps extends PageProps {
  item: Page
  q: string
}

export const getServerSideProps: GetServerSideProps<SearchResultsProps> = async (context) => {
  const apolloClient = initializeApolloFromContext(context)
  const post = await getSinglePost(
    apolloClient,
    PageType.Search,
    '/search/results',
    undefined,
    boostFromContext(context),
    context?.req?.thSplitTests,
  )
  const { page } = post.data

  if (!page) {
    return {
      notFound: true,
    }
  }

  if (page?.type === 'redirect_permanent' || page?.type === 'redirect_temporary') {
    return handleRedirect(context, page.url, page.type)
  }

  const q = trim(first(castArray(context.query.q)))

  if (q === '') {
    return {
      redirect: {
        destination: getStoreURLByContext(context, ''),
        permanent: false,
      },
    }
  }

  return {
    props: {
      ...await pageProps(
        context,
        {
          type: 'searchresults',
          id: '',
        },
        page,
        ['cart', 'cms', 'common', 'footer', 'giftcard', 'header', 'products', 'shipping', 'sidebar', 'validation'],
        true,
      ),
      item: page as Page,
      q,
    },
  }
}

const SearchResults = PageHOC<SearchResultsProps>(({
  item,
  q,
}) => {
  const components = useBlockComponents(item.blocks || [])

  return (
    <>
      <Head>
        <title>{`${item.seo?.title}: '${q}'`}</title>
      </Head>
      <OneColumn className={styles.successPage} footer={<FooterWithGroupVisibility />} header={<HeaderWithGroupVisibility />}>
        <SinglePostWrap>
          {components}
        </SinglePostWrap>
      </OneColumn>
      <BackToTop />
    </>
  )
})

export default SearchResults
